import {
  QUOTE_UPLOAD_CONTENT_TYPES,
  QuoteRequestInputSchema,
  quoteJobTypes,
  type QuoteRequestInput
} from '@dreams-built/shared/src/quotes/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import {
  useRequestUploadUrlMutation,
  useSubmitQuoteRequestMutation,
  uploadQuoteFile
} from 'api/quoting';
import { trackEvent } from 'features/Landing/analytics';
import { Container, PhoneLink } from 'features/Landing/primitives';
import { Faq } from 'features/Landing/sections/Faq';
import { useEffect, useRef, useState, type DragEvent, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { breadcrumbJsonLd, buildHead } from 'seo/head';

const JOB_TYPE_LABELS: Record<(typeof quoteJobTypes)[number], string> = {
  FOUNDATION: 'House foundation / slab',
  DRIVEWAY: 'Driveway',
  PATIO: 'Patio / paths',
  FENCING: 'Fencing / hard landscaping',
  REPAIR: 'Concrete repair',
  OTHER: 'Something else'
};

const QUOTE_FAQS = [
  {
    question: 'What happens after I submit?',
    answer:
      'Your request lands with the team immediately and you get a confirmation email. We review your plans or measurements and come back with a fixed, itemised quote, typically within two working days.'
  },
  {
    question: 'What files can I attach?',
    answer:
      'PDF plans and photos (JPG, PNG, WebP, HEIC) up to 25MB each. Floor plans and engineering drawings give the most accurate quote; for driveways and patios, photos of the area plus rough dimensions work well.'
  },
  {
    question: 'Is the quote binding?',
    answer:
      'The price is fixed for the scope quoted. If something genuinely unforeseeable comes up on site (like unexpected ground conditions), we talk to you before any extra work happens. No surprise invoices.'
  }
] as const;

const inputClasses =
  'w-full rounded-md border border-ink-300 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-600 focus:outline-2 focus:outline-offset-0 focus:outline-brand-500/30';

const labelClasses = 'mb-1.5 block text-sm font-medium text-ink-800';

const ACCEPTED_TYPES = new Set<string>(QUOTE_UPLOAD_CONTENT_TYPES);

// Files dragged from Finder or Explorer can arrive with no MIME type (HEIC photos in
// particular), so fall back to the extension.
const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic'
};

const withContentType = (file: File) => {
  if (file.type) return file;
  const type = TYPE_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() ?? ''];
  return type ? new File([file], file.name, { type, lastModified: file.lastModified }) : file;
};

const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes('Files');

const QuoteForm: FC = () => {
  const requestUploadUrl = useRequestUploadUrlMutation();
  const submitRequest = useSubmitQuoteRequestMutation();
  const [files, setFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'sent' | 'error'>('idle');
  const [fileError, setFileError] = useState('');
  const [dragging, setDragging] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);

  // A drop that misses the box would otherwise make the browser open the file and throw
  // away everything typed into the form.
  useEffect(() => {
    const ignoreStrayDrop = (event: globalThis.DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      if (!dropZoneRef.current?.contains(event.target as Node)) {
        event.dataTransfer.dropEffect = 'none';
      }
    };
    window.addEventListener('dragover', ignoreStrayDrop);
    window.addEventListener('drop', ignoreStrayDrop);
    return () => {
      window.removeEventListener('dragover', ignoreStrayDrop);
      window.removeEventListener('drop', ignoreStrayDrop);
    };
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<QuoteRequestInput>({
    resolver: zodResolver(QuoteRequestInputSchema),
    defaultValues: { jobType: 'FOUNDATION' }
  });

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    setFileError('');
    const accepted: File[] = [];
    for (const file of Array.from(incoming).map(withContentType)) {
      if (!ACCEPTED_TYPES.has(file.type)) {
        setFileError(`${file.name}: only PDF and image files are accepted`);
        continue;
      }
      if (file.size > 25 * 1024 * 1024) {
        setFileError(`${file.name} is over the 25MB limit`);
        continue;
      }
      accepted.push(file);
    }
    setFiles((current) => [...current, ...accepted].slice(0, 10));
  };

  const onSubmit = handleSubmit(async (input) => {
    setStatus('uploading');
    try {
      const uploaded = [];
      for (const file of files) {
        uploaded.push(
          await uploadQuoteFile((request) => requestUploadUrl.mutateAsync(request), file)
        );
      }
      await submitRequest.mutateAsync({ ...input, fileKeys: uploaded });
      reset();
      setFiles([]);
      setStatus('sent');
      trackEvent('quote_request_submit');
    } catch {
      setStatus('error');
    }
  });

  if (status === 'sent') {
    return (
      <div className="rounded-lg border border-ink-200 bg-white p-10 text-center">
        <p className="font-mono text-xs tracking-[0.2em] text-brand-600 uppercase">Received</p>
        <h2 className="font-display mt-3 text-3xl font-bold text-ink-900">
          Your quote request is in.
        </h2>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-ink-600">
          Check your inbox for a confirmation. We&rsquo;ll come back with a fixed quote,
          typically within two working days. Anything urgent, call{' '}
          <PhoneLink className="font-semibold text-brand-600" />.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-8 rounded-lg border border-ink-200 bg-white p-6 sm:p-10">
      <fieldset>
        <legend className="font-display text-lg font-bold text-ink-900">Who are you?</legend>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="q-name" className={labelClasses}>Name</label>
            <input id="q-name" autoComplete="name" className={inputClasses} {...register('name')} />
            {errors.name && <p role="alert" className="mt-1.5 text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label htmlFor="q-company" className={labelClasses}>Company (if you&rsquo;re a builder)</label>
            <input id="q-company" autoComplete="organization" className={inputClasses} {...register('company')} />
          </div>
          <div>
            <label htmlFor="q-email" className={labelClasses}>Email</label>
            <input id="q-email" type="email" autoComplete="email" className={inputClasses} {...register('email')} />
            {errors.email && <p role="alert" className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>}
          </div>
          <div>
            <label htmlFor="q-phone" className={labelClasses}>Phone</label>
            <input id="q-phone" type="tel" autoComplete="tel" className={inputClasses} {...register('phone')} />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend className="font-display text-lg font-bold text-ink-900">The job</legend>
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="q-jobtype" className={labelClasses}>Job type</label>
            <select id="q-jobtype" className={inputClasses} {...register('jobType')}>
              {quoteJobTypes.map((type) => (
                <option key={type} value={type}>
                  {JOB_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="q-target" className={labelClasses}>When do you need it?</label>
            <input id="q-target" placeholder="e.g. August, ASAP, flexible" className={inputClasses} {...register('targetDate')} />
          </div>
          <div>
            <label htmlFor="q-address" className={labelClasses}>Site address</label>
            <input id="q-address" autoComplete="street-address" className={inputClasses} {...register('siteAddress')} />
          </div>
          <div>
            <label htmlFor="q-city" className={labelClasses}>Town / city</label>
            <input id="q-city" placeholder="Hamilton, Cambridge…" className={inputClasses} {...register('city')} />
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="q-description" className={labelClasses}>Describe the job</label>
          <textarea
            id="q-description"
            rows={5}
            className={`${inputClasses} resize-y`}
            placeholder="What are we pouring or building? Anything about access, existing concrete to remove, finishes you want…"
            {...register('description')}
          />
          {errors.description && <p role="alert" className="mt-1.5 text-xs text-red-600">{errors.description.message}</p>}
        </div>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="q-area" className={labelClasses}>Floor / slab area (m², if known)</label>
            <input id="q-area" type="number" min="0" step="0.1" className={inputClasses} {...register('measurements.areaM2')} />
          </div>
          <div>
            <label htmlFor="q-perimeter" className={labelClasses}>Perimeter (lineal metres, if known)</label>
            <input id="q-perimeter" type="number" min="0" step="0.1" className={inputClasses} {...register('measurements.perimeterLm')} />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend className="font-display text-lg font-bold text-ink-900">Plans &amp; photos</legend>
        <p className="mt-1 text-sm text-ink-600">
          PDF plans or photos, up to 25MB each. Plans get you the most accurate quote.
        </p>
        <label
          ref={dropZoneRef}
          htmlFor="q-files"
          onDragEnter={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => {
            if (!hasFiles(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={(event) => {
            // Moving between the box's own children fires dragleave too; only reset when
            // the pointer actually leaves the box.
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            addFiles(event.dataTransfer.files);
          }}
          className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragging
              ? 'border-brand-500 bg-brand-50'
              : 'border-ink-300 bg-ink-50 hover:border-brand-500'
          }`}
        >
          <span className="text-sm font-semibold text-ink-800">
            {dragging ? 'Drop to add' : 'Drag plans or photos here, or click to browse'}
          </span>
          <span className="mt-1 font-mono text-xs text-ink-500">PDF · JPG · PNG · WEBP · HEIC</span>
          <input
            id="q-files"
            type="file"
            multiple
            accept={[...QUOTE_UPLOAD_CONTENT_TYPES, '.heic'].join(',')}
            className="sr-only"
            onChange={(event) => {
              addFiles(event.target.files);
              // Clear it so removing a file and picking the same one again still fires onChange.
              event.target.value = '';
            }}
          />
        </label>
        {fileError && <p role="alert" className="mt-2 text-xs text-red-600">{fileError}</p>}
        {files.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="flex items-center justify-between rounded-md border border-ink-200 px-3 py-2 text-sm">
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  className="ml-3 font-mono text-xs text-ink-500 uppercase hover:text-red-600"
                  onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </fieldset>

      {status === 'error' && (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Something went wrong submitting your request. Please try again, or call us on{' '}
          <PhoneLink className="font-semibold underline" />.
        </p>
      )}

      <button
        type="submit"
        disabled={isSubmitting || status === 'uploading'}
        className="w-full rounded-md bg-brand-600 px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-10"
      >
        {status === 'uploading' ? 'Uploading & sending…' : 'Request my free quote'}
      </button>
    </form>
  );
};

const QuotePage: FC = () => (
  <>
    <section className="bg-ink-900 pt-32 pb-16 sm:pt-40 sm:pb-20">
      <Container>
        <p className="font-mono text-xs tracking-[0.25em] text-brand-400 uppercase">
          Free fixed quotes - Hamilton &amp; the Waikato
        </p>
        <h1 className="font-display mt-4 max-w-3xl text-4xl leading-tight font-bold text-white sm:text-5xl">
          Send your plans, get a fixed quote
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-300 sm:text-lg">
          Foundations, driveways, patios, repairs: attach your plans or measurements and
          we&rsquo;ll price it properly. Typical turnaround: two working days.
        </p>
      </Container>
    </section>

    <section className="py-16 sm:py-24">
      <Container className="max-w-4xl">
        <QuoteForm />
      </Container>
    </section>

    <Faq faqs={QUOTE_FAQS} sheet="DB-02 - Before you ask" title="Quoting, answered" />
  </>
);

export const Route = createFileRoute('/_landing/quote')({
  head: () =>
    buildHead({
      title: 'Get a Fixed Quote | Concrete Foundations & Driveways – Dreams Built',
      description:
        'Upload your plans and get a fixed quote for concrete foundations, driveways or patios in Hamilton and the Waikato. Free, itemised, typically back within two working days.',
      path: '/quote',
      jsonLd: [
        breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Get a quote', path: '/quote' }
        ])
      ]
    }),
  component: QuotePage
});
